/**
 * Arena stats banner: dark scoreboard (Stadium Broadcast design).
 * Layout: [Title + streak top row] / [Ring + W/D/L stats | divider | Recent form]
 * Keeps all count-up animation logic, just rewrites the JSX/styles.
 */

import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { View, Text, Pressable, useWindowDimensions, InteractionManager, Platform } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { Flame } from 'lucide-react-native';
import Svg, { Circle, G } from 'react-native-svg';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../../src/theme/useTheme';
import type { ArenaStats } from '../../../src/challenges/types/arenaStats.types';
import {
  getWinsDrawsLossesRingProportions,
} from '../../../src/challenges/helpers/arenaStats.helper';
import {
  getArenaStatsBannerStyles,
  getArenaStatsBannerRingSize,
  getBannerGradientColors,
  getBannerTextureColors,
  getBannerOrbColors,
  getArenaRingTrackStroke,
  getArenaWdlRingStrokes,
} from './ArenaStatsBanner.styles';
import { ARENA_STATS_ERROR_COPY } from '../../../src/shared/constants/dataLoadError.constants';

const RING_RADIUS = 44;
const CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const ANIM_DURATION_MS = 1200;
const FILL_ANIM_DURATION_MS = 1200;
const COUNT_UP_TICK_MS = 32;

interface ArenaStatsBannerProps {
  stats: ArenaStats | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  testID?: string;
}

function useCountUp(
  targetValue: number,
  enabled: boolean,
  isInteger = false,
  durationMs = ANIM_DURATION_MS
): number {
  const [display, setDisplay] = useState(0);
  const startRef = useRef<number | null>(null);
  const lastTickRef = useRef(0);
  const frameRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setDisplay(targetValue);
      return;
    }

    startRef.current = null;
    lastTickRef.current = 0;
    setDisplay(0);

    const start = () => {
      const now = Date.now();
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - (1 - t) * (1 - t);
      const currentValue = eased * targetValue;
      const shouldTick = now - lastTickRef.current >= COUNT_UP_TICK_MS || t >= 1;
      if (shouldTick) {
        lastTickRef.current = now;
        setDisplay(isInteger ? Math.round(currentValue) : currentValue);
      }
      if (t < 1) frameRef.current = requestAnimationFrame(start);
    };

    frameRef.current = requestAnimationFrame(start);
    return () => cancelAnimationFrame(frameRef.current);
  }, [targetValue, enabled, isInteger, durationMs]);

  return enabled ? display : targetValue;
}

export const ArenaStatsBanner: React.FC<ArenaStatsBannerProps> = ({
  stats,
  isLoading,
  error,
  onRetry,
  testID,
}) => {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const ringSize = useMemo(() => getArenaStatsBannerRingSize(windowWidth), [windowWidth]);
  const styles = useMemo(() => getArenaStatsBannerStyles(theme, ringSize), [theme, ringSize]);
  const bannerGradients = useMemo(
    () => ({
      main: getBannerGradientColors(theme),
      texture: getBannerTextureColors(theme),
      orb: getBannerOrbColors(theme),
    }),
    [theme]
  );
  const ringTrackStroke = useMemo(() => getArenaRingTrackStroke(theme), [theme]);
  const wdlRingStrokes = useMemo(() => getArenaWdlRingStrokes(theme), [theme]);
  const [animationDone, setAnimationDone] = useState(false);
  const [animationDeferred, setAnimationDeferred] = useState(false);
  const deferredScheduledRef = useRef(false);
  const shouldAnimate = animationDeferred && !animationDone && stats != null && !isLoading;

  useEffect(() => {
    if (stats == null || isLoading || deferredScheduledRef.current) return;
    deferredScheduledRef.current = true;
    const interaction = InteractionManager.runAfterInteractions(() => {
      setAnimationDeferred(true);
    });
    const fallback = setTimeout(() => setAnimationDeferred(true), 400);
    return () => {
      interaction.cancel();
      clearTimeout(fallback);
    };
  }, [stats, isLoading]);

  const wins = stats?.wins ?? 0;
  const draws = stats?.draws ?? 0;
  const losses = stats?.losses ?? 0;
  const currentStreak = stats?.currentStreak ?? 0;
  const winRate = stats?.winRate ?? 0;
  const recentForm = stats?.recentForm ?? [];

  const countUpWins = useCountUp(wins, shouldAnimate, true);
  const countUpStreak = useCountUp(currentStreak, shouldAnimate, true);
  const countUpFill = useCountUp(1, shouldAnimate, false, FILL_ANIM_DURATION_MS);

  const displayWins = animationDeferred ? countUpWins : 0;
  const displayStreak = animationDeferred ? countUpStreak : 0;
  const fillProgress = animationDeferred ? countUpFill : 0;

  useEffect(() => {
    if (!shouldAnimate || !stats) return;
    const id = setTimeout(() => setAnimationDone(true), FILL_ANIM_DURATION_MS + 100);
    return () => clearTimeout(id);
  }, [shouldAnimate, stats]);

  const { winsLength, drawsLength, lossesLength, total: wdlTotal } =
    getWinsDrawsLossesRingProportions(wins, draws, losses);
  const displayWinsLength = winsLength * fillProgress;
  const displayDrawsLength = drawsLength * fillProgress;
  const displayLossesLength = lossesLength * fillProgress;

  const winRateDisplay = Math.round(winRate <= 1 ? winRate * 100 : winRate);

  const handleRetry = useCallback(() => {
    onRetry?.();
  }, [onRetry]);

  if (isLoading) {
    return (
      <View style={styles.skeletonWrapper} testID={testID ?? 'arena-stats-banner'}>
        <View style={styles.skeletonRow}>
          <View style={styles.skeletonCircle} />
          <View style={styles.skeletonLines}>
            <View style={styles.skeletonLine} />
            <View style={styles.skeletonLineShort} />
            <View style={styles.skeletonLine} />
          </View>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorWrapper} testID={testID ?? 'arena-stats-banner'}>
        <Text style={styles.errorTitle}>{ARENA_STATS_ERROR_COPY.TITLE}</Text>
        <Text style={styles.errorMessage}>{ARENA_STATS_ERROR_COPY.MESSAGE}</Text>
        {onRetry ? (
          <Pressable
            style={({ pressed, hovered }) => [
              styles.errorRetryButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.errorRetryButtonPressed,
              hovered && Platform.OS === 'web' && styles.errorRetryButtonHover,
            ]}
            onPress={handleRetry}
            accessibilityRole="button"
            accessibilityLabel="Try again"
            testID="arena-stats-banner-retry"
          >
            <Text style={styles.errorRetryButtonText}>Try again</Text>
          </Pressable>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.bannerWrapper} testID={testID ?? 'arena-stats-banner'}>
      {/* Dark gradient background */}
      <LinearGradient
        colors={bannerGradients.main}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradientFill}
      />
      {/* Diagonal texture overlay */}
      <LinearGradient
        colors={bannerGradients.texture}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.textureOverlay}
        pointerEvents="none"
      />
      {/* Top-right glow orb */}
      <LinearGradient
        colors={bannerGradients.orb}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={styles.glowOrb}
        pointerEvents="none"
      />

      <View style={styles.bannerContent}>
        {/* Top row: title (left) + streak badge (right) */}
        <View style={styles.topRow}>
          <View style={styles.topLeft}>
            <Text style={styles.seasonLabel}>Match Day Hub</Text>
            <Text style={styles.hubTitle}>Arena</Text>
          </View>
          {stats != null && currentStreak > 0 && (
            <View style={styles.streakBadge}>
              <Flame size={14} color={theme.accent} />
              <Text style={styles.streakText}>{displayStreak} streak</Text>
            </View>
          )}
        </View>

        {/* Bottom stats row: [Ring + record stats] | [divider] | [recent 1v1 form] */}
        <View style={styles.statsRow}>
          {/* Left: ring on left, W/D/L text to its right */}
          <View style={styles.ringAndStats}>
            <View style={styles.ringWrapper}>
              <Svg
                width={ringSize}
                height={ringSize}
                viewBox="0 0 100 100"
                style={styles.ringSvg}
              >
                {/* Track */}
                <Circle
                  cx={50}
                  cy={50}
                  r={RING_RADIUS}
                  stroke={ringTrackStroke}
                  strokeWidth={6}
                  fill="none"
                />
                {wdlTotal > 0 && (
                  <G transform="rotate(-90 50 50)">
                    {displayWinsLength > 0 && (
                      <Circle
                        cx={50}
                        cy={50}
                        r={RING_RADIUS}
                        stroke={wdlRingStrokes.wins}
                        strokeWidth={6}
                        fill="none"
                        strokeDasharray={`${displayWinsLength} ${CIRCUMFERENCE}`}
                        strokeDashoffset={0}
                        strokeLinecap="round"
                      />
                    )}
                    {displayDrawsLength > 0 && (
                      <Circle
                        cx={50}
                        cy={50}
                        r={RING_RADIUS}
                        stroke={wdlRingStrokes.draws}
                        strokeWidth={6}
                        fill="none"
                        strokeDasharray={`${displayDrawsLength} ${CIRCUMFERENCE}`}
                        strokeDashoffset={-displayWinsLength}
                        strokeLinecap="round"
                      />
                    )}
                    {displayLossesLength > 0 && (
                      <Circle
                        cx={50}
                        cy={50}
                        r={RING_RADIUS}
                        stroke={wdlRingStrokes.losses}
                        strokeWidth={6}
                        fill="none"
                        strokeDasharray={`${displayLossesLength} ${CIRCUMFERENCE}`}
                        strokeDashoffset={-(displayWinsLength + displayDrawsLength)}
                        strokeLinecap="round"
                      />
                    )}
                  </G>
                )}
              </Svg>
              {/* Center: wins number + WINS sub-label */}
              <View style={styles.centerContent}>
                <Text style={styles.winsCenter} accessibilityLabel={`${wins} wins`}>
                  {displayWins}
                </Text>
                <Text style={styles.winsCenterSub}>WINS</Text>
              </View>
            </View>

            {/* Stats text next to ring */}
            <View style={styles.statsColumn}>
              <Text style={styles.recordLabel}>1V1 Record</Text>
              <View style={styles.wdlRow}>
                <Text style={styles.winsNumber}>{wins}</Text>
                <Text style={styles.winsLabel}>W</Text>
                <Text style={styles.wdlSep}>·</Text>
                <Text style={styles.drawsText}>{draws}D</Text>
                <Text style={styles.wdlSep}>·</Text>
                <Text style={styles.lossesText}>{losses}L</Text>
              </View>
              {stats != null && (
                <Text style={styles.winRateText}>{winRateDisplay}% win rate</Text>
              )}
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Right: recent 1v1 form */}
          <View style={styles.rightSection}>
            <Text style={styles.formLabel}>Recent form</Text>
            {recentForm.length > 0 ? (
              <>
                <View style={styles.formPills}>
                  {recentForm.map((letter, index) => {
                    const chipStyle =
                      letter === 'W'
                        ? styles.formPillW
                        : letter === 'D'
                          ? styles.formPillD
                          : styles.formPillL;
                    const textStyle =
                      letter === 'W'
                        ? styles.formPillTextW
                        : letter === 'D'
                          ? styles.formPillTextD
                          : styles.formPillTextL;
                    return (
                      <View key={`${letter}-${index}`} style={[styles.formPill, chipStyle]}>
                        <Text style={[styles.formPillText, textStyle]}>{letter}</Text>
                      </View>
                    );
                  })}
                </View>
                <Text style={styles.formHintText}>Last 1v1 challenges</Text>
              </>
            ) : (
              <Text style={styles.noRightDataText}>No 1v1 results yet</Text>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
